import * as Sentry from '@sentry/react';
import { PartialLead } from '@superfunnel/types';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  endBefore,
  getDocs,
  limit,
  orderBy,
  Query,
  query,
  QueryDocumentSnapshot,
  startAfter,
} from 'firebase/firestore';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboardContext } from '../contexts/DashboardContext';
import { CustomDataGrid } from './CustomDataGrid/CustomDataGrid';

const PAGE_SIZE = 25;

interface Props {
  query: Query<PartialLead>;
}

const fetchPaginatedDocs = async function <T>(
  fQuery: Query<T>,
  pageParam: QueryDocumentSnapshot<T> | null,
  direction: 'next' | 'previous' = 'next',
) {
  const collectionRef = fQuery;
  let q;

  const order = orderBy('createdAt', 'desc');

  if (direction === 'next' && pageParam) {
    q = query(collectionRef, order, startAfter(pageParam), limit(PAGE_SIZE));
  } else if (direction === 'previous' && pageParam) {
    q = query(collectionRef, order, endBefore(pageParam), limit(PAGE_SIZE));
  } else {
    q = query(collectionRef, order, limit(PAGE_SIZE));
  }

  const snapshot = await getDocs(q);
  const docs: QueryDocumentSnapshot<T>[] = snapshot.docs;

  return docs;
};

export const PartialLeadsTable = ({ query }: Props) => {
  const navigate = useNavigate();
  const { dynamicPages, forms } = useDashboardContext();

  const { data, isFetching, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['partialLeads', query],
    queryFn: (ctx) =>
      fetchPaginatedDocs(query, ctx.pageParam).catch((e) => {
        Sentry.captureException(e);
        throw e;
      }),
    getNextPageParam: (group) => group[group.length - 1],
    initialPageParam: null as QueryDocumentSnapshot<PartialLead> | null,
    refetchOnWindowFocus: false,
    meta: {
      persist: false,
    },
  });

  const columns: ColumnDef<QueryDocumentSnapshot<PartialLead>>[] =
    useMemo(() => {
      return [
        {
          id: 'createdAt',
          header: 'Started At',
          accessorFn: (row) => row.data().createdAt?.toDate(),
          size: 180,
          cell: (info) => info.getValue()?.toLocaleString(),
        },
        {
          id: 'lastUpdatedAt',
          header: 'Last Updated',
          accessorFn: (row) => row.data().lastUpdatedAt?.toDate(),
          size: 180,
          cell: (info) => info.getValue()?.toLocaleString(),
        },
        {
          id: 'status',
          header: 'Status',
          accessorFn: (row) => {
            const data = row.data();
            if (data.convertedToLeadId) return 'converted';
            if (data.completedAt) return 'completed';
            return 'in-progress';
          },
          size: 120,
          cell: (info) => {
            const status = info.getValue() as string;
            return (
              <Badge
                variant={
                  status === 'converted'
                    ? 'success'
                    : status === 'completed'
                      ? 'blue'
                      : 'warning'
                }
                className="font-normal"
              >
                {status === 'converted'
                  ? 'Converted'
                  : status === 'completed'
                    ? 'Completed'
                    : 'In Progress'}
              </Badge>
            );
          },
        },
        // {
        //   id: 'currentStep',
        //   header: 'Step',
        //   accessorFn: (row) => row.data().currentStep,
        //   size: 80,
        //   cell: (info) => {
        //     const step = info.getValue() as number;
        //     return <span className="font-medium">{step}</span>;
        //   },
        // },
        {
          id: 'id',
          header: 'Submission ID',
          accessorFn: (row) => row.data().submissionId,
          size: 200,
          cell: (info) => {
            const partialLeadData = info.row.original.data();
            return (
              <div>
                <div className="mt-1 text-gray-500">
                  {partialLeadData.submissionId.slice(-8)}
                </div>
              </div>
            );
          },
        },
        {
          id: 'data',
          header: 'Data',
          accessorFn: (row) => row.data().form,
          size: 300,
          cell: (info) => {
            const partialLeadData = info.row.original.data();
            const formEntries = Object.entries(partialLeadData.form || {});
            if (formEntries.length === 0) {
              return <span className="text-gray-400">No data yet</span>;
            }
            const formatValue = (value: unknown): string => {
              if (value && typeof value === 'object') {
                const obj = value as Record<string, unknown>;
                if (obj.formattedAddress) return String(obj.formattedAddress);
                return JSON.stringify(value);
              }
              return `${value}`;
            };
            return (
              <Tooltip>
                <TooltipTrigger>
                  {formEntries
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([_, { value }]) => formatValue(value))
                    .join(', ')}
                </TooltipTrigger>
                <TooltipContent className="whitespace-pre-wrap">
                  <p>
                    {formEntries
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(
                        ([_, { label, value }]) =>
                          `${label}: ${formatValue(value)}`,
                      )
                      .join('\n')}
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          },
        },
        {
          id: 'url',
          header: 'Url',
          accessorFn: (row) => row.data().url,
          size: 200,
          cell: (info) => {
            const partialLeadData = info.row.original.data();
            return (
              <Tooltip>
                <TooltipTrigger className="max-w-full truncate">
                  {partialLeadData.url?.trim() || '-'}
                </TooltipTrigger>
                <TooltipContent className="break-all">
                  <p>{partialLeadData.url || '-'}</p>
                </TooltipContent>
              </Tooltip>
            );
          },
        },
        {
          id: 'form',
          header: 'Form',
          accessorFn: (row) => row.data().formId,
          size: 150,
          cell: (info) => {
            const partialLeadData = info.row.original.data();
            return (
              <Button
                variant="ghost"
                onClick={() => {
                  navigate('../forms');
                }}
                className="font-normal text-blue-500 underline"
              >
                {forms?.find((f) => f.id === partialLeadData.formId)?.data()
                  .name ??
                  partialLeadData.formId?.slice(-5) ??
                  '-'}
              </Button>
            );
          },
        },
        {
          id: 'dynamicPage',
          header: 'Dynamic Page',
          accessorFn: (row) => row.data().campaignId,
          size: 180,
          cell: (info) => {
            const partialLeadData = info.row.original.data();
            if (!partialLeadData.campaignId) {
              return <span className="text-gray-400">-</span>;
            }
            return (
              <Button
                variant="ghost"
                onClick={() => {
                  navigate(`../pages/${partialLeadData.campaignId}/details`);
                }}
                className="font-normal text-blue-500 underline"
              >
                {dynamicPages
                  ?.find((d) => d.id === partialLeadData.campaignId)
                  ?.data().name ?? partialLeadData.campaignId.slice(-5)}
              </Button>
            );
          },
        },
        {
          id: 'variant',
          header: 'Variant',
          accessorFn: (row) => row.data().websiteId,
          size: 150,
          cell: (info) => {
            const partialLeadData = info.row.original.data();
            if (!partialLeadData.variantId || !partialLeadData.campaignId) {
              return <span className="text-gray-400">-</span>;
            }
            return (
              <Button
                variant="ghost"
                onClick={() => {
                  navigate(
                    `../pages/${partialLeadData.campaignId}?tab=details&variantId=${partialLeadData.websiteId}`,
                  );
                }}
                className="font-normal text-blue-500 underline"
              >
                {partialLeadData.variantId.slice(-5)}
              </Button>
            );
          },
        },
        {
          id: 'convertedToLeadId',
          header: 'Lead ID',
          accessorFn: (row) => row.data().convertedToLeadId,
          size: 150,
          cell: (info) => {
            const convertedLeadId = info.getValue() as string | undefined;
            if (!convertedLeadId) {
              return <span className="text-gray-400">-</span>;
            }
            return (
              <span className="text-gray-600">{convertedLeadId.slice(-8)}</span>
            );
          },
        },
      ];
    }, [dynamicPages, navigate, forms]);

  return (
    <CustomDataGrid
      logicalName="partialLeads"
      columns={columns}
      data={data?.pages.flatMap((p) => p)}
      onReachedEnd={() => !isFetching && hasNextPage && fetchNextPage()}
      initialState={{
        columnVisibility: {
          id: false,
        },
      }}
    />
  );
};
